import { take } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';

import { Observable, of, Subscription } from 'rxjs';

import { getServerConfig } from '@app/origami-api/server-config';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { DialogService } from '@app/services/dialogs/dialog.service';
import {
  AdditionalRequestOptions,
  RequestProxy,
} from '@app/origami-api/services/base/request-proxy/request-proxy.class';
import { OrigamiResponse } from '../service.base';
import { CasAuth, GlobalEventService } from '@app/services/global-event.service';
import {
  BasicSimpleCredentialsPromptComponent,
  BasicSimpleCredentialsPromptDialogComponentResult,
} from '@app/services/dialogs/components/simple-credentials-prompt/basic-simple-credentials-prompt.component';
import { getErrorDescription } from '@app/origami-api/utils/error-utils';
import { UserValidationType } from '@app/origami-api/constants/user-validation-type';
import { ValidationData } from '@app/session/user-details';
import { MainSettingsModule } from '@app/services/settings/main';
import { ActionSource } from '@app/origami-api/constants/action-source';

export interface CASAuthRequestProxyData {
  errorMessage?: string;
  validationData?: ValidationData;
}

@Injectable()
export class CASAuthRequestProxy extends RequestProxy<CASAuthRequestProxyData, any> implements OnDestroy {
  private strings = tiStrings.tiComponents.documents.documentsActions.services.stringsESignatureActionsService;
  private miscStrings = tiStrings.app.misc;
  private subs = new Subscription();

  private _settings: MainSettingsModule;
  public set settings(v) {
    this._settings = v;
  }

  constructor(
    private readonly globalEventService: GlobalEventService,
    private readonly dialogService: DialogService,
  ) {
    super();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  protected isSimpleAuthMethodUsed(options?: AdditionalRequestOptions, response?: OrigamiResponse<any>) {
    const forcedAdditionalRequest = !!(options && options.forceAdditionalRequest && options.additionalRequest);
    const useSimpleValidationParamsDelivery = !!(options && options.useSimpleValidationParamsDelivery);
    return (forcedAdditionalRequest || useSimpleValidationParamsDelivery) && response.ErrorCode !== ErrorCode.SUCCESS;
  }

  protected needProxify(response: OrigamiResponse<any>, options?: AdditionalRequestOptions): boolean {
    const casEnabledWithSupportedErrors =
      getServerConfig().serverCfg.centralAuthentication === 'cas' &&
      [
        ErrorCode.USER_VALIDATION_REQUIRED,
        ErrorCode.VALIDATED_USER_DIFFERS_FROM_CURRENT,
        ErrorCode.USER_IDENTITY_VALIDATION_ERROR,
      ].indexOf(response.ErrorCode) !== -1;
    return casEnabledWithSupportedErrors || this.isSimpleAuthMethodUsed(options, response);
  }

  protected getParams(
    response: OrigamiResponse<any>,
    options?: AdditionalRequestOptions,
  ): Observable<{ cancel: boolean; data: CASAuthRequestProxyData }> {
    const useSimpleAuthMethod = this.isSimpleAuthMethodUsed(options, response);
    let authValidationUrl =
      (response.AuthValidationUrl
        ? response.AuthValidationUrl
        : response.Result && response.Result.AuthValidationUrl) || '';

    if (!(authValidationUrl || useSimpleAuthMethod)) {
      return of({ cancel: true, data: null });
    }

    return new Observable((obs) => {
      const showDialog = () => {
        // simple form
        if (useSimpleAuthMethod) {
          this.dialogService.showDialog(
            BasicSimpleCredentialsPromptComponent,
            (instance, close: (res: BasicSimpleCredentialsPromptDialogComponentResult) => void) => {
              if (!(options && options.useSimpleValidationParamsDelivery)) {
                instance.verificationRequest = options.additionalRequest;
                if (options.userName && options.password) {
                  instance.useSourceCredentials = true;
                  instance.loginName = options.userName;
                  instance.password = options.password;
                }
              }

              if (options?.source === ActionSource.TrueCopyCertification && this._settings) {
                instance.additionalText = this._settings.general.document.certifiedCopyReason.get();
              }

              instance.close.pipe(take(1)).subscribe((userData) => {
                if (userData && userData.done) {
                  // ok
                  const transactionId = null;
                  obs.next({
                    cancel: false, // !transactionId
                    data: {
                      validationData: {
                        TransactionId: transactionId,
                        UserName: userData.loginName,
                        UserPassword: userData.password,
                        ValidationType: UserValidationType.Internal,
                      },
                      errorMessage: transactionId ? null : this.strings.transactionIdWasNotFound,
                    },
                  });
                  obs.complete();
                  return close(userData);
                } else {
                  if (!(userData && userData.errorCode)) {
                    obs.next({
                      cancel: true,
                      data: null,
                    });
                    obs.complete();
                    return close(userData);
                  }
                  const errorMessage = userData
                    ? userData.errorMessage || getErrorDescription(userData.errorCode)
                    : getErrorDescription(undefined);
                  this.dialogService.showWarningModal(this.miscStrings.warning, errorMessage, null).then(() => {
                    close(userData);
                    return showDialog();
                  });
                }
              });
            },
          );
        } else {
          // cas based window
          if (getServerConfig().serverCfg.centralAuthentication === 'cas') {
            const win = window.open(
              authValidationUrl,
              '_blank',
              'resizable=yes,scrollbars=yes,status=yes,width=590,height=600',
            );

            // let's detect if user closed the popup
            const detectionInterval = setInterval(() => {
              if (!win || (win && win.closed)) {
                setTimeout(() => {
                  clearInterval(detectionInterval);
                  obs.next({
                    cancel: true,
                    data: {
                      validationData: {
                        TransactionId: null,
                      },
                      errorMessage: this.strings.transactionIdWasNotFound,
                    },
                  });
                  obs.complete();
                }, 500);
              }
            }, 1000);

            this.subs.add(
              this.globalEventService
                .on<string>(CasAuth.Events.end)
                .pipe(take(1))
                .subscribe((transactionId) => {
                  obs.next({
                    cancel: !transactionId,
                    data: {
                      validationData: {
                        TransactionId: transactionId,
                      },
                      errorMessage: transactionId ? null : this.strings.transactionIdWasNotFound,
                    },
                  });
                  obs.complete();
                  win && win.close();
                }),
            );
          }
        }
      };

      if (response.ErrorCode === ErrorCode.VALIDATED_USER_DIFFERS_FROM_CURRENT) {
        this.dialogService
          .showWarningModal(this.miscStrings.warning, this.strings.userIdentityIsWrongMessage, null)
          .then(() => showDialog());
      } else {
        showDialog();
      }
    });
  }
}
